import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { deleteStaffStart, getAdminRolesStart, getAllStaffStart, updateStaffStatusStart } from '../../../../store/redux-slices/staffSlice'
import { tableHeaders } from '../constants'
import { toast } from '../../../../components/Toast'
import { use } from 'react'

const useStaffListing = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Local state
  const [limit, setLimit] = useState(15)
  const [page, setPage] = useState(1)
  const [orderBy, setOrderBy] = useState('adminUserId')
  const [search, setSearch] = useState('')
  const [staffStatus, setStaffStatus] = useState("")
  const [staffRole, setStaffRole] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sort, setSort] = useState('desc')
  const [adminUserId, setAdminUserId] = useState()
  const [active, setActive] = useState()
  const [show, setShow] = useState(false)
  const [deleteModalShow, setDeleteModalShow] = useState(false)
  const [staffId, setStaffId] = useState('')
  // Add state for selected staff member details
  const [selectedStaff, setSelectedStaff] = useState(null)

  const staffState = useSelector(state => state?.staff) || {}
  const { data: staffData = null, loading: isStaffLoading = false } = staffState

  const safeStaffData = staffData || { result: [], pagination: {} }
  const { result: staffList = [] } = safeStaffData

  const totalPages = safeStaffData?.pagination?.totalPages
  const totalCount = safeStaffData?.pagination?.total

  const loginState = useSelector(state => state?.login) || {}
  const { adminRole: adminRoles = [], loading: isRolesLoading = false } = loginState

  // Custom debounce implementation
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)

    return () => clearTimeout(timer)
  }, [search])

  // Fetch staff list when dependencies change
  useEffect(() => {
    const params = {
      pageNo: page,
      limit,
      orderBy,
      sort,
      roleId: staffRole, status: staffStatus
    }

    if (debouncedSearch) {
      params.search = debouncedSearch
    }

    if (!isValidOrderBy(orderBy)) return

    dispatch(getAllStaffStart(params))
  }, [page, limit, debouncedSearch, orderBy, sort, staffRole, staffStatus,])

  // Fetch admin roles once on component mount
  useEffect(() => {
    // Only fetch admin roles if they haven't been loaded yet
    if (!adminRoles || adminRoles.length === 0) {
      dispatch(getAdminRolesStart())
    }
  }, [dispatch, adminRoles])

  const onHandleResetFilter = () => {
    setSearch('');
    setStaffStatus("");
    setStaffRole("");
  }

  const getRole = (id) =>
    adminRoles && adminRoles.find((obj) => obj.adminRoleId === id)?.name

  const handleShow = (staffMember) => {
    setStaffId(staffMember.adminUserId)
    setActive(staffMember.isActive)
    setSelectedStaff({
      name: `${staffMember.firstName} ${staffMember.lastName}`,
      email: staffMember.email,
      adminUserId: staffMember.adminUserId
    })
    setShow(true)
  }

  const handleYes = () => {
    const data = {
      data: {
        adminProfileId: staffId,
        isActive: !active,
      },
      onSuccess: () => {
        toast('Status updated successfully', 'success')
        setShow(false)
        setSelectedStaff(null)

        // Refresh the staff list after status update
        const params = {
          pageNo: page,
          limit,
          orderBy,
          sort
        }

        if (debouncedSearch) {
          params.search = debouncedSearch
        }

        dispatch(getAllStaffStart(params))
      },
      onError: (message) => {
        toast(message || 'Something went wrong', 'error')
        setShow(false)
        setSelectedStaff(null)
      }
    }

    dispatch(updateStaffStatusStart(data))
  }

  const selected = (h) =>
    orderBy === h.value &&
    h.label !== 'Role' &&
    h.label !== 'Status' &&
    h.label !== 'Action' &&
    h.label !== 'Group'

  // Updated handleDeleteModal to store staff details
  const handleDeleteModal = (staffMember) => {
    setStaffId(staffMember.adminUserId)
    setSelectedStaff({
      name: `${staffMember.firstName} ${staffMember.lastName}`,
      email: staffMember.email,
      adminUserId: staffMember.adminUserId
    })
    setDeleteModalShow(true)
  }

  const handleDeleteYes = () => {
    dispatch(deleteStaffStart({
      data: {
        adminProfileId: staffId
      },
      onSuccess: () => {
        toast('Staff deleted successfully', 'success')
        setDeleteModalShow(false)
        setSelectedStaff(null)

        // Refresh the staff list after deletion
        const params = {
          pageNo: page,
          limit,
          orderBy,
          sort
        }

        if (debouncedSearch) {
          params.search = debouncedSearch
        }

        dispatch(getAllStaffStart(params))
      },
      onError: (error) => {
        toast(error.message || 'Something went wrong', 'error')
        setDeleteModalShow(false)
        setSelectedStaff(null)
      }
    }))
  }

  const isValidOrderBy = (orderBy) => {
    // Return true if the orderBy field is sortable (not in the excluded headers)
    const excludedHeaders = tableHeaders.slice(-4).map(h => h.value) // Last 4 headers: Role, Group, Status, Action
    return !excludedHeaders.includes(orderBy)
  }

  return {
    navigate,
    limit,
    setLimit,
    page,
    setPage,
    setOrderBy,
    sort,
    setSort,
    search,
    setSearch,
    show,
    setShow,
    data: staffList,
    totalPages,
    getRole,
    handleShow,
    handleYes,
    selected,
    selectedStaff, // Return selectedStaff instead of selected for modal
    active,
    loading: isRolesLoading || isStaffLoading,
    handleDeleteModal,
    handleDeleteYes,
    deleteModalShow,
    setDeleteModalShow,
    isValidOrderBy,
    staffStatus, setStaffStatus,
    staffRole, setStaffRole,
    adminRoles,
    onHandleResetFilter,
    totalCount
  }
}

export default useStaffListing
